// src/pages/HomePage.js
import React, { useState, useEffect } from 'react';
import '../styles/HomePage.css';
import { Link } from 'react-router-dom';
import {
  MIXCLOUD_USERNAME,
  MIXCLOUD_API_BASE,
  ALL_EPISODES_SLUG,
} from '../config/mixcloud';

const useBannerData = () => {
  const [banners, setBanners] = useState([
    {
      desktop: {
        image: 'https://mmo.aiircdn.com/2302/68f7fa9b7ea03.jpg',
        alt: 'Utter Radio',
        width: '100%',
        className: 'welcome-image',
      },
      mobile: {
        image: 'https://mmo.aiircdn.com/2302/68f7fa9b7ea03.jpg',
        alt: 'Welcome',
        className: 'mobile-logo',
      },
    },
  ]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadBanners = async () => {
      try {
        const response = await fetch(`${process.env.PUBLIC_URL}/banner.json`, {
          cache: 'no-store',
        });

        if (!response.ok) {
          throw new Error(`Failed to load banner.json (${response.status})`);
        }

        const data = await response.json();

        const bannerArray = Array.isArray(data) ? data : [data];
        setBanners(bannerArray);
      } catch (error) {
        console.warn('Using default banner configuration:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadBanners();
  }, []);

  useEffect(() => {
    if (banners.length <= 1 || isLoading) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % banners.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [banners.length, isLoading]);

  return { banners, currentIndex, setCurrentIndex, isLoading };
};

const usePresentersData = () => {
  const [presenters, setPresenters] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const url = `${process.env.PUBLIC_URL}/presenters.json`;
        const res = await fetch(url, { cache: 'no-store' });

        if (!res.ok)
          throw new Error(`Failed to load presenters.json (${res.status})`);

        const data = await res.json();
        if (!cancelled) setPresenters(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Presenters load error:', e);
        if (!cancelled) setPresenters([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return { presenters, isLoading };
};

// Custom hook to load "Who Are We" content from JSON
const useWhoAreWeData = () => {
  const [content, setContent] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadContent = async () => {
      try {
        const response = await fetch(
          `${process.env.PUBLIC_URL}/whoarewe.json`,
          { cache: 'no-store' }
        );

        if (!response.ok) {
          throw new Error(`Failed to load whoarewe.json (${response.status})`);
        }

        const data = await response.json();

        setContent((prev) => ({
          title: data.title || prev.title,
          subtitle: data.subtitle || prev.subtitle,
          description: data.description || prev.description,
          features: Array.isArray(data.features) ? data.features : prev.features,
          image: data.image || prev.image,
        }));
      } catch (error) {
        console.warn("Using default 'Who Are We' content:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadContent();
  }, []);

  return { content, isLoading };
};

// Who Are We component
const WhoAreWe = () => {
  const { content, isLoading } = useWhoAreWeData();

  if (isLoading) {
    return (
      <div className="unified-card">
        <div className="unified-card-header">
          <h3>Who Are We?</h3>
        </div>
        <div className="unified-card-body">
          <div className="loading-container">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="unified-card who-are-we-card">
      <div className="unified-card-header">
        <h3>{content.title || 'Who Are We?'}</h3>
      </div>
      <div className="unified-card-body">
        <div className="who-are-we-content">
          <div className="who-are-we-text">
            {content.subtitle && (
              <h4 className="who-are-we-subtitle">{content.subtitle}</h4>
            )}
            <p className="who-are-we-description">{content.description}</p>

            {content.features && content.features.length > 0 && (
              <div className="who-are-we-features">
                {content.features.map((feature, index) => (
                  <div className="feature-item" key={index}>
                    <div className="feature-icon">
                      <i className={`fas ${feature.icon || 'fa-check'}`}></i>
                    </div>
                    <div className="feature-text">
                      <h5>{feature.title}</h5>
                      <p>{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {content.image && (
            <div className="who-are-we-image">
              <img
                src={content.image}
                alt={content.title || 'Who Are We'}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src =
                    'https://mmo.aiircdn.com/2302/68f7fa9b7ea03.jpg';
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// -------------------------------------------------------------------------
//  Recent Episodes — now sourced from Mixcloud (all cloudcasts)
// -------------------------------------------------------------------------

const createSlug = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

const formatMixcloudDuration = (seconds) => {
  if (!seconds || Number.isNaN(seconds)) return '0:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) {
    return `${h}:${m < 10 ? '0' : ''}${m}:${s < 10 ? '0' : ''}${s}`;
  }
  return `${m}:${s < 10 ? '0' : ''}${s}`;
};

const RecentEpisodes = () => {
  const [episodes, setEpisodes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const formatDate = (date) => {
    if (!date || date.getFullYear() === 1970) return 'No date';

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const compareDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );

    const diffTime = today - compareDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;

    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const loadRecentEpisodes = async () => {
    try {
      // Fetch a small batch of cloudcasts (newest first by default) — no
      // need to pull the whole catalogue just to show 3 cards.
      const url = `${MIXCLOUD_API_BASE}/${MIXCLOUD_USERNAME}/cloudcasts/?limit=10`;
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) throw new Error('Failed to fetch episodes from Mixcloud');

      const data = await response.json();
      const cloudcasts = Array.isArray(data?.data) ? data.data : [];

      if (cloudcasts.length === 0) {
        setEpisodes([]);
        return;
      }

      // Normalize into the shape the card expects.
      const normalized = cloudcasts.map((cloudcast) => {
        const title = cloudcast.name || 'Untitled';

        // Cloudcast pictures provide several sizes; pick the best available.
        const imageUrl =
          cloudcast.pictures?.large ||
          cloudcast.pictures?.medium ||
          cloudcast.pictures?.small ||
          cloudcast.user?.pictures?.large ||
          cloudcast.user?.pictures?.medium ||
          '/UtterRadioLogo.png';

        // `created_time` is an ISO timestamp — no need to parse it out of
        // the title anymore.
        const date = cloudcast.created_time
          ? new Date(cloudcast.created_time)
          : new Date(1970, 0, 1);

        return {
          id: cloudcast.key || `${title}-${cloudcast.created_time}`,
          // Fall back to the account name if the cloudcast doesn't embed a user.
          showTitle: cloudcast.user?.name || MIXCLOUD_USERNAME,
          showImage: imageUrl,
          episodeTitle: title,
          episodeDescription: cloudcast.description || '',
          // Kept for future use / analytics if we ever want it
          cloudcastKey: cloudcast.key,
          cloudcastUrl: cloudcast.url,
          duration: formatMixcloudDuration(cloudcast.audio_length),
          date,
          slug: createSlug(title),
        };
      });

      // Belt-and-braces: sort by date descending in case the API order shifts.
      normalized.sort((a, b) => b.date - a.date);

      // Only the 3 most recent.
      setEpisodes(normalized.slice(0, 3));
    } catch (error) {
      console.error('Error loading recent episodes:', error);
      setEpisodes([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRecentEpisodes();
  }, []);

  return (
    <div className="unified-card">
      <div className="unified-card-header">
        <h3>Recent Catchup Episodes</h3>
        <Link to="/catchup" className="unified-btn">
          View All
        </Link>
      </div>
      <div className="unified-card-body">
        <div className="episodes-list">
          {isLoading ? (
            Array(3)
              .fill(0)
              .map((_, index) => (
                <div className="episode-item loading" key={index}>
                  <div className="episode-art">
                    <div className="loading-art"></div>
                  </div>
                  <div className="episode-info">
                    <span className="loading-text"></span>
                    <h4 className="loading-text"></h4>
                  </div>
                  <div className="play-button loading"></div>
                </div>
              ))
          ) : episodes.length === 0 ? (
            <div className="no-episodes">
              <i className="fas fa-headphones-slash"></i>
              <p>No recent episodes available</p>
            </div>
          ) : (
            episodes.map((episode) => {
              const formattedDate = formatDate(episode.date);
              const imageUrl = episode.showImage || '/UtterRadioLogo.png';

              // New slug-based URL so the CatchupPage routes it directly
              // to the All Episodes view and auto-plays that episode.
              const linkTo = `/catchup/${ALL_EPISODES_SLUG}/${episode.slug}`;

              return (
                <Link to={linkTo} className="episode-item" key={episode.id}>
                  <img
                    src={imageUrl}
                    alt={episode.episodeTitle}
                    className="episode-art-img"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/UtterRadioLogo.png';
                    }}
                  />
                  <div className="episode-info">
                    <h4
                      className="episode-title"
                      title={episode.episodeTitle}
                    >
                      {episode.episodeTitle}
                    </h4>
                    <div className="episode-meta">
                      <span className="episode-date">
                        <i className="far fa-calendar"></i> {formattedDate}
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

// Unified Presenters component
const Presenters = ({ presenters = [] }) => {
  if (!presenters.length) return null;

  return (
    <section className="unified-presenters">
      <div className="unified-section-header">
        <h2>Meet The Team</h2>
        <p className="unified-section-subtitle">
          Nothing at Utter Radio would be possible without the dedicated and
          passionate team of students behind the scenes
        </p>
      </div>

      <div className="unified-presenters-list">
        {presenters.map((presenter, index) => (
          <div
            key={presenter.id || presenter.name || index}
            className="unified-presenter"
          >
            <div className="unified-presenter-image-container">
              <img
                src={
                  presenter.image ||
                  'https://via.placeholder.com/400x400/333333/ffffff?text=Presenter'
                }
                alt={presenter.name || 'Presenter'}
                className="unified-presenter-image"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src =
                    'https://via.placeholder.com/400x400/333333/ffffff?text=Presenter';
                }}
              />
            </div>

            <div className="unified-presenter-content">
              <div className="unified-presenter-header">
                <h3 className="unified-presenter-name">{presenter.name}</h3>
                <span className="unified-presenter-role">{presenter.role}</span>
              </div>

              <p className="unified-presenter-description">
                {presenter.description}
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

// Main HomePage component
export const HomePage = () => {
  const { presenters, isLoading: presentersLoading } = usePresentersData();
  const {
    banners,
    currentIndex,
    setCurrentIndex,
    isLoading: bannerLoading,
  } = useBannerData();

  if (bannerLoading && presentersLoading) {
    return (
      <div className="loading-container">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="homepage-wrapper">
      {/* Welcome Banner dotsel */}
      <div className="welcome-banner">
        <div className="dotsel-container">
          {banners.map((banner, index) => (
            <picture
              key={index}
              className={`dotsel-slide ${index === currentIndex ? 'active' : ''}`}
            >
              <source media="(max-width: 768px)" srcSet={banner.mobile.image} />
              <img
                src={banner.desktop.image}
                alt={banner.desktop.alt || `Utter Radio Banner ${index + 1}`}
                className="welcome-image"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src =
                    'https://mmo.aiircdn.com/2302/68f7fa9b7ea03.jpg';
                }}
              />
            </picture>
          ))}

          {banners.length > 1 && (
            <div className="dotsel-indicators">
              {banners.map((_, index) => (
                <button
                  key={index}
                  className={`dotsel-dot ${index === currentIndex ? 'active' : ''}`}
                  onClick={() => setCurrentIndex(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Stacked Cards */}
      <div className="cards-stack">
        <WhoAreWe />
        <RecentEpisodes />
      </div>

      <Presenters presenters={presenters} />
    </div>
  );
};